/*
 * V1 → V2 typography token mapping data and types.
 *
 * Mapping types:
 *   direct  — exact tier correspondence: category + size tier maps 1:1 to a V2 style.
 *   nearest — no exact V2 size exists; matched by priority: family → weight → size
 *             (closest font family, then closest weight, then closest font size).
 *
 * Mono variants share the same v2 target as their standard counterpart.
 * Platform teams apply the mono transform after resolving the mapped v2 style:
 *   - fontFamily → 'UberMoveMono'
 *   - letterSpacing → 0
 *   - fontWeight → resolve nearest available weight
 * (Stefan confirmed platform teams handle mono independently of Figma's logic layer.)
 *
 * Use this data for:
 *   - JS/TS auto-upgrade codemods
 *   - GoWeb Tailwind compat bridge (future)
 *   - Lint rules flagging un-migrated v1 tokens
 */

import type { TypographyRampKey } from '../generated/typography/v2/typography';

// ---------------------------------------------------------------------------
// V1 standard key type
// Derived from the CSS custom properties in base-typography.css (kebab → camelCase).
// ---------------------------------------------------------------------------

export type V1TypographyKey =
// Paragraph — UberMoveText, regular weight
'paragraphXsmall' // 12px / line-height 20px
| 'paragraphSmall' // 14px / line-height 20px
| 'paragraphMedium' // 16px / line-height 24px
| 'paragraphLarge' // 18px / line-height 28px
// Label — UberMoveText, medium weight
| 'labelXsmall' // 12px / line-height 16px
| 'labelSmall' // 14px / line-height 16px
| 'labelMedium' // 16px / line-height 20px
| 'labelLarge' // 18px / line-height 24px
// Heading — UberMove, bold weight
| 'headingXsmall' // 20px / line-height 28px
| 'headingSmall' // 24px / line-height 32px
| 'headingMedium' // 28px / line-height 36px
| 'headingLarge' // 32px / line-height 40px
| 'headingXlarge' // 36px / line-height 44px
| 'headingXxlarge' // 40px / line-height 52px
// Display — UberMove, bold weight
| 'displayXsmall' // 36px / line-height 44px
| 'displaySmall' // 44px / line-height 52px
| 'displayMedium' // 52px / line-height 64px
| 'displayLarge'; // 96px / line-height 112px

// ---------------------------------------------------------------------------
// V1 mono key type — mirrors the standard set with a 'mono' prefix.
// ---------------------------------------------------------------------------

export type V1MonoTypographyKey =
// Mono Paragraph — UberMoveMono, regular weight
'monoParagraphXsmall' | 'monoParagraphSmall' | 'monoParagraphMedium' | 'monoParagraphLarge'
// Mono Label — UberMoveMono, medium weight
| 'monoLabelXsmall' | 'monoLabelSmall' | 'monoLabelMedium' | 'monoLabelLarge'
// Mono Heading — UberMoveMono, bold weight
| 'monoHeadingXsmall' | 'monoHeadingSmall' | 'monoHeadingMedium' | 'monoHeadingLarge' | 'monoHeadingXlarge' | 'monoHeadingXxlarge'
// Mono Display — UberMoveMono, bold weight
| 'monoDisplayXsmall' | 'monoDisplaySmall' | 'monoDisplayMedium' | 'monoDisplayLarge';
export type V2TypographyKey = TypographyRampKey;

/** Whether the mapping is a 1:1 tier match or a nearest-available approximation. */
export type TypographyMappingType = 'direct' | 'nearest';
export type TypographyMapping = {
  v2: V2TypographyKey;
  mapping: TypographyMappingType;
  /** Human-readable note explaining why a nearest match was chosen. */
  notes?: string;
};

/**
 * Mapping descriptor for a mono v1 key.
 * Mono styles resolve to the same v2 target as their standard counterpart.
 * After resolving, apply the mono transform:
 *   fontFamily → 'UberMoveMono', letterSpacing → 0, fontWeight → nearest available.
 */
export type MonoTypographyMapping = TypographyMapping & {
  /** The standard (non-mono) v1 key this mono style derives from. */
  standardKey: V1TypographyKey;
  monoTransform: true;
};

// ---------------------------------------------------------------------------
// Standard V1 → V2 mapping table
// ---------------------------------------------------------------------------

export const V1_TO_V2_TYPOGRAPHY = (({
  // --- Display (UberMove, bold) — all nearest: v2 caps at 24px ---
  displayLarge: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '96px → 24px'
  },
  displayMedium: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '52px → 24px'
  },
  displaySmall: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '44px → 24px'
  },
  displayXsmall: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '36px → 24px'
  },
  // --- Heading (UberMove, bold) ---
  headingXxlarge: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '40px → 24px'
  },
  headingXlarge: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '36px → 24px'
  },
  headingLarge: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '32px → 24px'
  },
  headingMedium: {
    v2: 'headlineXlBold',
    mapping: 'nearest',
    notes: '28px → 24px'
  },
  headingSmall: {
    v2: 'headlineXlBold',
    mapping: 'direct'
  },
  headingXsmall: {
    v2: 'headlineLBold',
    mapping: 'direct'
  },
  // --- Label (UberMoveText, medium weight) ---
  labelLarge: {
    v2: 'headlineMMedium',
    mapping: 'direct'
  },
  labelMedium: {
    v2: 'headlineSMedium',
    mapping: 'direct'
  },
  labelSmall: {
    v2: 'headlineXsMedium',
    mapping: 'direct'
  },
  labelXsmall: {
    v2: 'headlineXxsMedium',
    mapping: 'direct'
  },
  // --- Paragraph (UberMoveText, regular weight) ---
  paragraphLarge: {
    v2: 'bodyLRegular',
    mapping: 'nearest',
    notes: '18px → 16px'
  },
  paragraphMedium: {
    v2: 'bodyLRegular',
    mapping: 'direct'
  },
  paragraphSmall: {
    v2: 'bodyMRegular',
    mapping: 'direct'
  },
  paragraphXsmall: {
    v2: 'bodySRegular',
    mapping: 'direct'
  }
} as const) satisfies Record<V1TypographyKey, TypographyMapping>);

// ---------------------------------------------------------------------------
// Mono V1 → V2 mapping table
// Derived from the standard table — mono styles map to identical v2 targets.
// Only the standardKey back-reference and monoTransform marker are added.
// ---------------------------------------------------------------------------

const MONO_KEY_TO_STANDARD_KEY: Record<V1MonoTypographyKey, V1TypographyKey> = {
  monoDisplayLarge: 'displayLarge',
  monoDisplayMedium: 'displayMedium',
  monoDisplaySmall: 'displaySmall',
  monoDisplayXsmall: 'displayXsmall',
  monoHeadingXxlarge: 'headingXxlarge',
  monoHeadingXlarge: 'headingXlarge',
  monoHeadingLarge: 'headingLarge',
  monoHeadingMedium: 'headingMedium',
  monoHeadingSmall: 'headingSmall',
  monoHeadingXsmall: 'headingXsmall',
  monoLabelLarge: 'labelLarge',
  monoLabelMedium: 'labelMedium',
  monoLabelSmall: 'labelSmall',
  monoLabelXsmall: 'labelXsmall',
  monoParagraphLarge: 'paragraphLarge',
  monoParagraphMedium: 'paragraphMedium',
  monoParagraphSmall: 'paragraphSmall',
  monoParagraphXsmall: 'paragraphXsmall'
};
export const V1_TO_V2_MONO_TYPOGRAPHY = (Object.fromEntries((Object.entries(MONO_KEY_TO_STANDARD_KEY) as [V1MonoTypographyKey, V1TypographyKey][]).map(([monoKey, standardKey]) => [monoKey, {
  ...V1_TO_V2_TYPOGRAPHY[standardKey],
  standardKey,
  monoTransform: (true as const)
}])) as Record<V1MonoTypographyKey, MonoTypographyMapping>);
export type V1ToV2TypographyMap = typeof V1_TO_V2_TYPOGRAPHY;
export type V1ToV2MonoTypographyMap = typeof V1_TO_V2_MONO_TYPOGRAPHY;
export type MappedV2TypographyKey = V1ToV2TypographyMap[V1TypographyKey]['v2'];